import React, { useState, useEffect } from 'react';
import { emailListsAPI, adminAPI } from '../../services/api';

const EmailListsEditor = () => {
  const [emailLists, setEmailLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [availableBrands, setAvailableBrands] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [showMembersPreview, setShowMembersPreview] = useState(null);
  const [membersPreview, setMembersPreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    domain_filters: [],
    member_ids: [],
  });

  useEffect(() => {
    fetchEmailLists();
    fetchBrands();
    fetchUsers();
  }, []);

  const fetchEmailLists = async () => {
    setLoading(true);
    try {
      const data = await emailListsAPI.getLists();
      setEmailLists(data);
    } catch (err) {
      console.error('Error fetching email lists:', err);
      setMessage({ type: 'error', text: 'Failed to load email lists' });
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const data = await adminAPI.getAllowedDomains();
      const brands = data
        .filter(d => d.is_active && d.domain && !d.domain.includes('drummond'))
        .map(d => d.domain);
      setAvailableBrands(brands);
    } catch (err) {
      console.error('Error fetching brands:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await adminAPI.getUsers();
      setAllUsers(data.filter(u => u.is_active));
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const handleDomainToggle = (domain) => {
    setFormData(prev => {
      const newDomains = prev.domain_filters.includes(domain)
        ? prev.domain_filters.filter(d => d !== domain)
        : [...prev.domain_filters, domain];
      return { ...prev, domain_filters: newDomains };
    });
  };

  const handleMemberToggle = (userId) => {
    setFormData(prev => {
      const newMembers = prev.member_ids.includes(userId)
        ? prev.member_ids.filter(id => id !== userId)
        : [...prev.member_ids, userId];
      return { ...prev, member_ids: newMembers };
    });
  };

  const isUserIncludedByDomain = (user) => {
    return formData.domain_filters.includes(user.domain);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingId) {
        await emailListsAPI.updateList(editingId, formData);
        setMessage({ type: 'success', text: 'Email list updated successfully' });
      } else {
        await emailListsAPI.createList(formData);
        setMessage({ type: 'success', text: 'Email list created successfully' });
      }
      resetForm();
      fetchEmailLists();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to save email list',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (list) => {
    setFormData({
      name: list.name,
      description: list.description || '',
      domain_filters: list.domain_filters || [],
      member_ids: list.manual_member_ids || [],
    });
    setEditingId(list.id);
    setShowForm(true);
  };

  const handleToggleActive = async (list) => {
    try {
      await emailListsAPI.updateList(list.id, { is_active: !list.is_active });
      setMessage({
        type: 'success',
        text: `Email list ${list.is_active ? 'deactivated' : 'activated'}`,
      });
      fetchEmailLists();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to update email list',
      });
    }
  };

  const handleDelete = async (list) => {
    if (!window.confirm(`Are you sure you want to delete "${list.name}"? This will also remove it from any announcements using this list.`))
      return;

    try {
      await emailListsAPI.deleteList(list.id);
      setMessage({ type: 'success', text: 'Email list deleted successfully' });
      fetchEmailLists();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to delete email list',
      });
    }
  };

  const handleViewMembers = async (list) => {
    if (showMembersPreview === list.id) {
      setShowMembersPreview(null);
      setMembersPreview(null);
      return;
    }
    try {
      const data = await emailListsAPI.getMembers(list.id);
      setMembersPreview(data);
      setShowMembersPreview(list.id);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load members' });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      domain_filters: [],
      member_ids: [],
    });
    setEditingId(null);
    setShowForm(false);
    setUserSearch('');
  };

  // Filter users for the manual members section
  const filteredUsers = allUsers.filter(user => {
    if (!userSearch) return true;
    const search = userSearch.toLowerCase();
    return (
      (user.email && user.email.toLowerCase().includes(search)) ||
      (user.full_name && user.full_name.toLowerCase().includes(search))
    );
  });

  return (
    <div className="email-lists-editor">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">Email Lists</h4>
          <p className="text-muted mb-0">
            Create curated email lists to target announcements to specific groups of users.
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => { resetForm(); setShowForm(true); }}
        >
          + New Email List
        </button>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'danger' : 'success'} alert-dismissible`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">{editingId ? 'Edit Email List' : 'New Email List'}</h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g. Monthly Newsletter Recipients"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Optional description"
                  />
                </div>
              </div>

              {/* Domain Filters */}
              <div className="mb-3">
                <label className="form-label">Domain Filters</label>
                <p className="text-muted small mb-2">
                  Select domains to automatically include all users from those organisations.
                </p>
                {availableBrands.length === 0 ? (
                  <p className="text-muted small">No brand domains configured.</p>
                ) : (
                  <div className="row">
                    {availableBrands.map((domain) => (
                      <div className="col-md-4 col-lg-3" key={domain}>
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`domain-${domain}`}
                            checked={formData.domain_filters.includes(domain)}
                            onChange={() => handleDomainToggle(domain)}
                          />
                          <label className="form-check-label" htmlFor={`domain-${domain}`}>
                            {domain}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Manual Members */}
              <div className="mb-3">
                <label className="form-label">Manual Members</label>
                <p className="text-muted small mb-2">
                  Individually select users to include. Users already covered by domain filters are marked.
                </p>
                <input
                  type="text"
                  className="form-control form-control-sm mb-2"
                  placeholder="Search users by name or email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />
                <div style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.375rem', padding: '0.5rem' }}>
                  {filteredUsers.length === 0 ? (
                    <p className="text-muted small mb-0">No users found.</p>
                  ) : (
                    filteredUsers.map(user => {
                      const viaDomain = isUserIncludedByDomain(user);
                      const isManual = formData.member_ids.includes(user.id);
                      return (
                        <div className="form-check" key={user.id}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`user-${user.id}`}
                            checked={isManual}
                            onChange={() => handleMemberToggle(user.id)}
                          />
                          <label className="form-check-label" htmlFor={`user-${user.id}`}>
                            {user.full_name || user.email}
                            <span className="text-muted ms-1" style={{ fontSize: '0.8em' }}>
                              ({user.email})
                            </span>
                            {viaDomain && !isManual && (
                              <span className="badge bg-info ms-1" style={{ fontSize: '0.7em' }}>via domain</span>
                            )}
                          </label>
                        </div>
                      );
                    })
                  )}
                </div>
                {formData.member_ids.length > 0 && (
                  <small className="text-muted mt-1 d-block">
                    {formData.member_ids.length} manual member{formData.member_ids.length !== 1 ? 's' : ''} selected
                  </small>
                )}
              </div>

              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</>
                  ) : editingId ? 'Update Email List' : 'Create Email List'}
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Email Lists Table */}
      <div className="card">
        <div className="card-body p-0">
          {loading && !showForm ? (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm"></div>
            </div>
          ) : emailLists.length === 0 ? (
            <p className="text-muted text-center py-4 mb-0">
              No email lists yet. Create your first one!
            </p>
          ) : (
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th style={{ width: '180px' }}>Domain Filters</th>
                  <th style={{ width: '100px' }}>Members</th>
                  <th style={{ width: '80px' }}>Status</th>
                  <th style={{ width: '220px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {emailLists.map((list) => (
                  <React.Fragment key={list.id}>
                    <tr className={!list.is_active ? 'table-secondary' : ''}>
                      <td className="fw-semibold">{list.name}</td>
                      <td>
                        <small className="text-muted">
                          {list.description || '-'}
                        </small>
                      </td>
                      <td>
                        {list.domain_filters && list.domain_filters.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {list.domain_filters.map(d => (
                              <span key={d} className="badge bg-light text-dark border" style={{ fontSize: '0.75em' }}>
                                {d}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <small className="text-muted">None</small>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-link p-0"
                          onClick={() => handleViewMembers(list)}
                          title="View resolved members"
                        >
                          {list.effective_member_count} member{list.effective_member_count !== 1 ? 's' : ''}
                        </button>
                      </td>
                      <td>
                        {list.is_active ? (
                          <span className="badge bg-success">Active</span>
                        ) : (
                          <span className="badge bg-secondary">Inactive</span>
                        )}
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => handleEdit(list)}
                          >
                            Edit
                          </button>
                          <button
                            className={`btn ${list.is_active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                            onClick={() => handleToggleActive(list)}
                          >
                            {list.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(list)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                    {showMembersPreview === list.id && membersPreview && (
                      <tr>
                        <td colSpan="6" className="bg-light">
                          <div className="p-2">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <strong>Effective Members ({membersPreview.total})</strong>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => { setShowMembersPreview(null); setMembersPreview(null); }}
                              >
                                Close
                              </button>
                            </div>
                            {membersPreview.members.length === 0 ? (
                              <p className="text-muted mb-0 small">No members resolved.</p>
                            ) : (
                              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                <table className="table table-sm table-bordered mb-0">
                                  <thead>
                                    <tr>
                                      <th>Name</th>
                                      <th>Email</th>
                                      <th>Source</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {membersPreview.members.map(m => (
                                      <tr key={m.id}>
                                        <td>{m.full_name || '-'}</td>
                                        <td>{m.email}</td>
                                        <td>
                                          <span className={`badge ${m.source === 'manual' ? 'bg-primary' : 'bg-info'}`}>
                                            {m.source}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="row mt-4">
        <div className="col-md-4">
          <div className="card bg-light">
            <div className="card-body text-center">
              <h3 className="mb-0">{emailLists.length}</h3>
              <small className="text-muted">Total Lists</small>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card bg-light">
            <div className="card-body text-center">
              <h3 className="mb-0 text-success">
                {emailLists.filter(l => l.is_active).length}
              </h3>
              <small className="text-muted">Active</small>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card bg-light">
            <div className="card-body text-center">
              <h3 className="mb-0" style={{ color: 'var(--info-color)' }}>
                {emailLists.reduce((sum, l) => sum + (l.effective_member_count || 0), 0)}
              </h3>
              <small className="text-muted">Total Members (across all lists)</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailListsEditor;
